import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useScrollLock } from "../../../hooks/useScrollLock";

import { FiPackage, FiEdit, FiTrash2, FiPlus, FiX } from "react-icons/fi";

import {
  StatusBadge,
  PageHeader,
  Button,
  FormInput,
  FormGroup,
  FormLabel,
  FormTextarea,
  Modal,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalTitle,
  PageSection,
  PageTitle,
  ProductActions,
  ProductGrid,
  StatCard,
  StatLabel,
  StatsGrid,
  StatValue,
  NewsCard,
  ProductsCount,
  CategoryDescription,
  CategoryTitle,
  CategoryHeader,
  CategoryActionsContainer,
  CategoryFeatures,
  CategoryFeature,
  CheckboxContainer,
  CheckboxLabel,
  FormActionsContainer,
  ModalOverlay,
  ConfirmBox,
  CloseBtn,
  ModalActions,
  Cancel,
  Confirm,
} from "./AdminCategoryPage.styles";

import AdminContainer from "../../../components/utils/AdminContainer";

import { selectCategories } from "../../../store/slices/products/slice";
import { AnimatePresence, motion } from "framer-motion";
import { FaStar, FaTimes } from "react-icons/fa";
import {
  createCategory,
  deleteCategory,
  editCategory,
  toggleCategoryStatus,
} from "../../../store/slices/products/asyncThunks";
import Loader from "../../../components/utils/Loader";
import { toast } from "../../../components/modern-ui/sonner";

const AdminCategoryPage = () => {
  const dispatch = useDispatch();
  const {
    loading,
    data,
    editLoading,
    createLoading,
    deleteLoading,
    toggleLoading,
  } = useSelector(selectCategories);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeModal, setActiveModal] = useState(null);

  const [deleteCategoryModal, setDeleteCategoryModal] = useState(false);
  const [categoryId, setCategoryId] = useState(null);

  const [categoryName, setCategoryName] = useState("");
  const [categoryDesc, setCategoryDesc] = useState("");
  const [categoryActive, setCategoryActive] = useState(false);
  const [hasSizes, setHasSizes] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);

  useScrollLock(activeModal || deleteCategoryModal);

  // Modal functions
  const closeModal = () => {
    setActiveModal(null);
    setSelectedCategory(null);
  };

  const openAddModal = () => {
    setSelectedCategory(null);
    setActiveModal("addCategory");
  };

  const openEditModal = (category) => {
    setCategoryName(category.name);
    setCategoryDesc(category.description);
    setCategoryActive(category.active);
    setSelectedCategory(category);
    setHasSizes(category.hasSizes);
    setIsFeatured(category.isFeatured);
    setActiveModal("editCategory");
  };

  // Form submission
  const handleCategorySubmit = (e) => {
    dispatch(
      createCategory({
        name: categoryName,
        description: categoryDesc,
        active: categoryActive,
        hasSizes: hasSizes,
        isFeatured: isFeatured,
      })
    )
      .unwrap()
      .then(() => {
        setCategoryName("");
        setCategoryActive(false);
        setCategoryDesc("");
        setHasSizes(false);
        setIsFeatured(false);
        closeModal();
      })
      .catch((err) => toast.error(err.message));
  };

  // Calculate statistics
  const stats = {
    total: data.length,
    active: data.filter((c) => c.active).length,
    totalProducts: data.reduce((sum, c) => sum + c.numOfProducts, 0),
    withProducts: data.filter((c) => c.numOfProducts > 0).length,
  };

  // Toggle category status
  const toggleCategoryStatusHandler = (categoryId, active) => {
    dispatch(toggleCategoryStatus(categoryId))
      .unwrap()
      .then(() =>
        toast.success(
          `Uspešno ste ${active ? "deaktivirali" : "aktivirali"}  kategoriju.`
        )
      )
      .catch((err) => toast.error(err.message));
  };

  const handleEditCategory = () => {
    if (!selectedCategory) return;

    // Uporedi vrednosti
    const hasChanges =
      (categoryName && categoryName !== selectedCategory.name) ||
      (categoryDesc && categoryDesc !== selectedCategory.description) ||
      categoryActive !== selectedCategory.active ||
      hasSizes !== selectedCategory.hasSizes ||
      isFeatured !== selectedCategory.isFeatured;

    if (!hasChanges) {
      toast.warning("⚠️ Nema izmena, ne saljem request.");
      closeModal();
      return;
    }

    dispatch(
      editCategory({
        id: selectedCategory.id,
        requestData: {
          name: categoryName || selectedCategory.name,
          description: categoryDesc || selectedCategory.description,
          active:
            categoryActive !== undefined
              ? categoryActive
              : selectedCategory.active,
          hasSizes:
            hasSizes !== undefined ? hasSizes : selectedCategory.hasSizes,
          isFeatured:
            isFeatured !== undefined ? isFeatured : selectedCategory.isFeatured,
        },
      })
    )
      .unwrap()
      .then(() => {
        toast.success("Uspešno ste izmenili kategoriju.");
        setCategoryName("");
        setCategoryDesc("");
        setCategoryActive(false);
        setHasSizes(false);
        setIsFeatured(false);
        closeModal();
      })
      .catch((err) => toast.error(err.message));
  };

  // Render loading state
  if (loading && !data.length) {
    return (
      <AdminContainer>
        <PageSection>
          <PageHeader>
            <PageTitle>Upravljanje Kategorijama</PageTitle>
          </PageHeader>
          <Loader title={"kategorija"} />
        </PageSection>
      </AdminContainer>
    );
  }

  return (
    <>
      <AdminContainer>
        <PageSection>
          <PageHeader>
            <PageTitle>Upravljanje Kategorijama</PageTitle>
            <Button $variant="primary" onClick={openAddModal}>
              <FiPlus />
              Dodaj Novu Kategoriju
            </Button>
          </PageHeader>

          <StatsGrid>
            <StatCard>
              <StatValue>{stats.total}</StatValue>
              <StatLabel>Ukupno Kategorija</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.active}</StatValue>
              <StatLabel>Aktivne Kategorije</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.totalProducts}</StatValue>
              <StatLabel>Ukupno Proizvoda</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.withProducts}</StatValue>
              <StatLabel>Kategorije sa Proizvodima</StatLabel>
            </StatCard>
          </StatsGrid>

          <ProductGrid>
            {[...data]
              .sort((a, b) => {
                // Aktivne kategorije prvo (true = 1, false = 0)
                if (a.active === b.active) return 0;
                return b.active - a.active;
              })
              .map((category) => (
                <NewsCard key={category.id} $active={category.active}>
                  <CategoryHeader>
                    <CategoryTitle>{category.name}</CategoryTitle>
                    <CategoryActionsContainer>
                      <StatusBadge
                        $status={category.active ? "active" : "inactive"}
                      >
                        {category.active ? "Aktivna" : "Neaktivna"}
                      </StatusBadge>
                    </CategoryActionsContainer>
                  </CategoryHeader>

                  <CategoryDescription>
                    {category.description || "Nema opisa kategorije"}
                  </CategoryDescription>

                  <CategoryFeatures>
                    <CategoryFeature $active={category.hasSizes}>
                      <FiPackage size={12} />
                      {category.hasSizes ? "Ima veličine" : "Nema veličine"}
                    </CategoryFeature>

                    <CategoryFeature $active={category.isFeatured}>
                      <FaStar /> {category.isFeatured ? "Istaknuto" : "Obično"}
                    </CategoryFeature>

                    <ProductsCount>
                      <FiPackage size={14} />
                      {category.numOfProducts} proizvoda
                    </ProductsCount>
                  </CategoryFeatures>

                  <ProductActions>
                    <Button
                      $variant="secondary"
                      onClick={() => openEditModal(category)}
                    >
                      <FiEdit size={16} />
                      Izmeni
                    </Button>
                    {toggleLoading ? (
                      <Button
                        $disabled
                        $variant={category.active ? "danger" : "success"}
                      >
                        {category.active ? "Deaktiviraj" : "Aktiviraj"}
                        <l-line-spinner
                          size="16"
                          stroke="2"
                          speed="1"
                          color="#fff"
                        />
                      </Button>
                    ) : (
                      <Button
                        $variant={category.active ? "danger" : "success"}
                        onClick={() =>
                          toggleCategoryStatusHandler(
                            category.id,
                            category.active
                          )
                        }
                      >
                        {category.active ? "Deaktiviraj" : "Aktiviraj"}
                      </Button>
                    )}
                    <Button
                      $variant="danger"
                      onClick={() => {
                        setCategoryId(category.id);
                        setDeleteCategoryModal(true);
                      }}
                    >
                      <FiTrash2 size={16} />
                      Obriši
                    </Button>
                  </ProductActions>
                </NewsCard>
              ))}
          </ProductGrid>

          {/* Add/Edit Category Modal */}
          <Modal
            $active={
              activeModal === "addCategory" || activeModal === "editCategory"
            }
          >
            <ModalContent>
              <ModalHeader>
                <ModalTitle>
                  {activeModal === "editCategory"
                    ? "Izmeni Kategoriju"
                    : "Dodaj Novu Kategoriju"}
                </ModalTitle>
                <ModalClose onClick={closeModal}>
                  <FiX />
                </ModalClose>
              </ModalHeader>

              <div>
                <FormGroup>
                  <FormLabel>Naziv Kategorije</FormLabel>
                  <FormInput
                    type="text"
                    name="categoryName"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    placeholder="Unesite naziv kategorije..."
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>Opis Kategorije</FormLabel>
                  <FormTextarea
                    name="categoryDescription"
                    value={categoryDesc}
                    placeholder="Kratak opis kategorije proizvoda..."
                    onChange={(e) => setCategoryDesc(e.target.value)}
                    required
                  />
                </FormGroup>

                <FormGroup style={{ marginBottom: 0 }}>
                  <CheckboxContainer>
                    <input
                      type="checkbox"
                      name="hasSizes"
                      id="hasSizes"
                      checked={hasSizes}
                      onChange={(e) => setHasSizes(e.target.checked)}
                    />

                    <CheckboxLabel htmlFor="hasSizes">
                      Ima veličine
                    </CheckboxLabel>
                  </CheckboxContainer>
                </FormGroup>

                <FormGroup>
                  <CheckboxContainer>
                    <input
                      type="checkbox"
                      name="isFeatured"
                      id="isFeatured"
                      checked={isFeatured}
                      onChange={(e) => setIsFeatured(e.target.checked)}
                    />

                    <CheckboxLabel htmlFor="isFeatured">
                      Istakni kategoriju (Featured)
                    </CheckboxLabel>
                  </CheckboxContainer>
                </FormGroup>

                <FormGroup>
                  <CheckboxContainer>
                    <input
                      type="checkbox"
                      name="categoryActive"
                      id="categoryActive"
                      checked={categoryActive}
                      onChange={(e) => setCategoryActive(e.target.checked)}
                    />

                    <CheckboxLabel htmlFor="categoryActive">
                      Aktivna kategorija (vidljiva u prodavnici)
                    </CheckboxLabel>
                  </CheckboxContainer>
                </FormGroup>

                <FormActionsContainer>
                  {editLoading || createLoading ? (
                    <Button $disabled>
                      {activeModal === "editCategory"
                        ? "Sačuvaj Izmene"
                        : "Dodaj Kategoriju"}
                      <l-line-spinner
                        size="16"
                        stroke="2"
                        speed="1"
                        color="#fff"
                      />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        if (activeModal === "editCategory") {
                          handleEditCategory();
                        } else {
                          handleCategorySubmit();
                        }
                      }}
                    >
                      {activeModal === "editCategory"
                        ? "Sačuvaj Izmene"
                        : "Dodaj Kategoriju"}
                    </Button>
                  )}
                  <Button onClick={closeModal}>Otkaži</Button>
                </FormActionsContainer>
              </div>
            </ModalContent>
          </Modal>
        </PageSection>
      </AdminContainer>
      <AnimatePresence>
        {deleteCategoryModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (!deleteLoading) {
                setDeleteCategoryModal(false);
              }
            }}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn
                onClick={() => {
                  if (!deleteLoading) {
                    setDeleteCategoryModal(false);
                  }
                }}
              >
                <FaTimes />
              </CloseBtn>
              <h3>Potvrdi brisanje</h3>
              <p>Da li si siguran da želiš da obrišeš ovu kategoriju?</p>
              <ModalActions>
                <Cancel
                  onClick={() => {
                    if (!deleteLoading) {
                      setDeleteCategoryModal(false);
                    }
                  }}
                >
                  Otkaži
                </Cancel>
                {!deleteLoading ? (
                  <Confirm
                    onClick={() => {
                      dispatch(deleteCategory(categoryId))
                        .unwrap()
                        .then(() => {
                          toast.success("Uspešno ste obrisali kategoriju.");
                          setDeleteCategoryModal(false);
                        })
                        .catch((err) => toast.error(err.message));
                    }}
                  >
                    Obriši
                  </Confirm>
                ) : (
                  <Confirm $disabled>
                    Obriši
                    <l-line-spinner
                      size="16"
                      stroke="2"
                      speed="1"
                      color="#fff"
                    />
                  </Confirm>
                )}
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminCategoryPage;
